import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, Download, RefreshCw, Undo2 } from 'lucide-react';
import {
  WASFATY_ALTERNATIVE_REASONS,
  formatMoney,
  type WasfatyAuthorizationDto,
  type WasfatyAuthorizationResultDto,
  type WasfatyDownloadResultDto,
  type WasfatyLineDto,
  type WasfatyPrescriptionDto,
} from '@his/shared';
import { get, post } from '@/lib/api';
import { dateTime, display } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Checkbox, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { describeIntegrationError, reportOutcome } from './external-outcome';
import {
  STATUS_TEXT,
  approvedLines,
  choiceProblem,
  holdCountdown,
  initialChoices,
  lineTotalMinor,
  requestFrom,
  requestProblem,
  stageOf,
  statusTone,
  totalMinor,
  tradeOf,
  withTrade,
  type LineChoice,
} from './wasfaty-flow';

const RESUME_KEY = 'his.wasfaty.resume';

function rememberedPrescription(): string | null {
  try {
    return window.localStorage.getItem(RESUME_KEY);
  } catch {
    return null;
  }
}

function remember(id: string | null): void {
  try {
    if (id) window.localStorage.setItem(RESUME_KEY, id);
    else window.localStorage.removeItem(RESUME_KEY);
  } catch {
    return;
  }
}

const riyal = (minor: number | null | undefined) => formatMoney(minor ?? 0, 'SAR', display.locale);

interface Notice {
  variant: 'warning' | 'destructive' | 'success';
  text: string;
}

export function WasfatyDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [reference, setReference] = useState('');
  const [patientIdentity, setPatientIdentity] = useState('');
  const [prescription, setPrescription] = useState<WasfatyPrescriptionDto | null>(null);
  const [choices, setChoices] = useState<Record<number, LineChoice>>({});
  const [instructions, setInstructions] = useState<Record<number, string>>({});
  const [notice, setNotice] = useState<Notice | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const refreshedFor = useRef<string | null>(null);
  const [resumeId, setResumeId] = useState<string | null>(() => rememberedPrescription());

  const authorization = prescription?.latestAuthorization ?? null;
  const stage = stageOf(authorization);
  const countdown = holdCountdown(authorization?.holdExpiresAt ?? null, now);
  const holding = Boolean(authorization?.holdExpiresAt) && (stage === 'awaiting' || stage === 'claim' || stage === 'declined');

  const resumable = useQuery({
    queryKey: ['wasfaty-resume', resumeId],
    queryFn: () => get<WasfatyPrescriptionDto>(`/integrations/wasfaty/prescriptions/${resumeId}`),
    enabled: Boolean(resumeId) && !prescription,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (resumable.isError) {
      remember(null);
      setResumeId(null);
    }
  }, [resumable.isError]);

  useEffect(() => {
    if (!holding) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') setNow(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [holding]);

  function show(next: WasfatyPrescriptionDto, fresh: boolean): void {
    setPrescription(next);
    if (fresh) {
      setChoices(initialChoices(next.lines));
      setInstructions({});
    }
    setNotice(null);
    remember(next.id);
    setResumeId(next.id);
  }

  function showAuthorization(next: WasfatyAuthorizationDto | null): void {
    setPrescription((current) => (current ? { ...current, latestAuthorization: next } : current));
    if (next?.status === 'dispensed') remember(null);
  }

  function noticeFrom(outcome: { status: string; offline: boolean; message: string }): void {
    if (outcome.status === 'succeeded') return setNotice(null);
    setNotice({ variant: outcome.offline ? 'warning' : 'destructive', text: outcome.message });
  }

  const { mutate: refreshPrescription } = useMutation({
    mutationFn: (id: string) => get<WasfatyPrescriptionDto>(`/integrations/wasfaty/prescriptions/${id}`),
    onSuccess: (next) => setPrescription(next),
  });

  const lapsedAuthorizationId = holding && countdown.expired ? (authorization?.id ?? null) : null;
  const prescriptionId = prescription?.id ?? null;
  useEffect(() => {
    if (!lapsedAuthorizationId || !prescriptionId || refreshedFor.current === lapsedAuthorizationId) return;
    refreshedFor.current = lapsedAuthorizationId;
    refreshPrescription(prescriptionId);
  }, [lapsedAuthorizationId, prescriptionId, refreshPrescription]);

  const download = useMutation({
    mutationFn: () => post<WasfatyDownloadResultDto>('/integrations/wasfaty/prescriptions/download', { reference: reference.trim(), patientIdentity: patientIdentity.trim() }),
    onSuccess: (outcome) => {
      if (outcome.prescription) show(outcome.prescription, true);
      noticeFrom(outcome);
    },
    onError: (caught) => setNotice({ variant: 'destructive', text: describeIntegrationError(caught) }),
  });

  const authorize = useMutation({
    mutationFn: () => post<WasfatyAuthorizationResultDto>(`/integrations/wasfaty/prescriptions/${prescription?.id}/authorizations`, requestFrom(prescription?.lines ?? [], choices)),
    onSuccess: (outcome) => {
      if (outcome.authorization) showAuthorization(outcome.authorization);
      noticeFrom(outcome);
      reportOutcome(toast, outcome, 'Request sent to the payer');
    },
    onError: (caught) => setNotice({ variant: 'destructive', text: describeIntegrationError(caught) }),
  });

  const check = useMutation({
    mutationFn: () => post<WasfatyAuthorizationResultDto>(`/integrations/wasfaty/authorizations/${authorization?.id}/check`),
    onSuccess: (outcome) => {
      if (outcome.authorization) showAuthorization(outcome.authorization);
      noticeFrom(outcome);
      if (outcome.status === 'succeeded') toast.success('Checked', outcome.message);
    },
    onError: (caught) => setNotice({ variant: 'destructive', text: describeIntegrationError(caught) }),
  });

  const release = useMutation({
    mutationFn: () => post<WasfatyAuthorizationResultDto>(`/integrations/wasfaty/authorizations/${authorization?.id}/release`, {}),
    onSuccess: (outcome) => {
      if (outcome.authorization) showAuthorization(outcome.authorization);
      noticeFrom(outcome);
      reportOutcome(toast, outcome, 'Prescription released');
    },
    onError: (caught) => setNotice({ variant: 'destructive', text: describeIntegrationError(caught) }),
  });

  const claim = useMutation({
    mutationFn: () => {
      const typed: Record<string, string> = {};
      for (const [lineNo, text] of Object.entries(instructions)) if (text.trim()) typed[lineNo] = text.trim();
      return post<WasfatyAuthorizationResultDto>(`/integrations/wasfaty/authorizations/${authorization?.id}/claim`, { instructions: typed });
    },
    onSuccess: (outcome) => {
      if (outcome.authorization) showAuthorization(outcome.authorization);
      noticeFrom(outcome);
      reportOutcome(toast, outcome, 'Claim accepted');
    },
    onError: (caught) => setNotice({ variant: 'destructive', text: describeIntegrationError(caught) }),
  });

  const busy = download.isPending || authorize.isPending || check.isPending || release.isPending || claim.isPending;
  const problem = prescription ? requestProblem(prescription.lines, choices) : null;
  const resume = resumable.data && !prescription && resumable.data.latestAuthorization && stageOf(resumable.data.latestAuthorization) !== 'choose' && stageOf(resumable.data.latestAuthorization) !== 'done' ? resumable.data : null;

  function startOver(): void {
    remember(null);
    setResumeId(null);
    setPrescription(null);
    setNotice(null);
    setReference('');
    setPatientIdentity('');
  }

  const footer = (() => {
    if (!prescription) {
      return (
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => download.mutate()} loading={download.isPending} disabled={reference.trim().length < 3 || patientIdentity.trim().length < 5} className="gap-1.5">
            <Download className="h-4 w-4" /> Download prescription
          </Button>
        </>
      );
    }
    if (stage === 'choose') {
      return (
        <>
          <Button variant="ghost" onClick={startOver}>
            Another prescription
          </Button>
          <Button onClick={() => authorize.mutate()} loading={authorize.isPending} disabled={Boolean(problem) || busy}>
            Ask the payer to authorise · {riyal(totalMinor(prescription.lines, choices))}
          </Button>
        </>
      );
    }
    if (stage === 'awaiting') {
      return (
        <>
          <Button variant="ghost" onClick={() => release.mutate()} loading={release.isPending} disabled={busy} className="gap-1.5">
            <Undo2 className="h-4 w-4" /> Release prescription
          </Button>
          <Button onClick={() => check.mutate()} loading={check.isPending} disabled={busy} className="gap-1.5">
            <RefreshCw className="h-4 w-4" /> Check answer
          </Button>
        </>
      );
    }
    if (stage === 'claim') {
      return (
        <>
          <Button variant="ghost" onClick={() => release.mutate()} loading={release.isPending} disabled={busy} className="gap-1.5">
            <Undo2 className="h-4 w-4" /> Release prescription
          </Button>
          <Button onClick={() => claim.mutate()} loading={claim.isPending} disabled={busy || countdown.expired} className="gap-1.5">
            <CheckCircle2 className="h-4 w-4" /> Handed over: submit the claim
          </Button>
        </>
      );
    }
    if (stage === 'declined') {
      return (
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => release.mutate()} loading={release.isPending} disabled={busy} className="gap-1.5">
            <Undo2 className="h-4 w-4" /> Release prescription
          </Button>
        </>
      );
    }
    return (
      <>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button onClick={startOver}>Another prescription</Button>
      </>
    );
  })();

  return (
    <Dialog open={open} onClose={onClose} wide title="Wasfaty prescription" description="Downloaded, authorised by the payer and claimed through the Wasfaty hub, from this computer's own internet connection" footer={footer}>
      <div className="space-y-4">
        {notice && <Alert variant={notice.variant}>{notice.text}</Alert>}

        {!prescription && (
          <>
            {resume && (
              <Alert variant="warning" title="A prescription is still open">
                <div className="flex flex-wrap items-center gap-2">
                  <span>
                    <span className="font-mono">{resume.reference}</span> · {resume.patientName ?? 'Patient'} · {STATUS_TEXT[resume.latestAuthorization?.status ?? 'unknown']}
                  </span>
                  <Button size="sm" variant="outline" className="ms-auto" onClick={() => show(resume, true)}>
                    Continue
                  </Button>
                </div>
              </Alert>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Prescription reference" required hint="From the patient's Wasfaty message or the printed slip">
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Reference number" autoFocus />
              </Field>
              <Field label="Patient national ID or Iqama" required>
                <Input value={patientIdentity} onChange={(e) => setPatientIdentity(e.target.value)} inputMode="numeric" />
              </Field>
            </div>
            <p className="text-xs text-muted-foreground">Wasfaty limits how many times a prescription can be viewed. Download it once, then carry on from here.</p>
          </>
        )}

        {prescription && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border bg-muted/30 px-3 py-2 text-sm">
            <span className="font-mono font-semibold">{prescription.reference}</span>
            <span>{prescription.patientName ?? 'Patient'}</span>
            <span className="text-muted-foreground">ID {prescription.patientIdentity}</span>
            <span className="text-muted-foreground">Prescriber {prescription.prescriberName ?? '—'}</span>
            <span className="text-muted-foreground">Downloaded {dateTime(prescription.downloadedAt)}</span>
            {authorization && (
              <Badge variant={statusTone(authorization.status)} className="ms-auto">
                {STATUS_TEXT[authorization.status]}
              </Badge>
            )}
          </div>
        )}

        {prescription && holding && (
          <Alert variant={countdown.expired ? 'destructive' : 'default'}>
            {countdown.expired ? 'The 25-minute hold ran out. Refreshing…' : `This prescription is held for this pharmacy. ${countdown.label} left.`}
          </Alert>
        )}

        {prescription && stage === 'choose' && (
          <div className="space-y-3">
            {authorization && <Alert variant="warning">{authorization.cancellationReason ?? authorization.comments ?? STATUS_TEXT[authorization.status]}. You can authorise it again below.</Alert>}
            {prescription.lines.map((line) => (
              <LineCard key={line.lineNo} line={line} choice={choices[line.lineNo]} onChange={(next) => setChoices((current) => ({ ...current, [line.lineNo]: next }))} />
            ))}
            {problem && <p className="text-xs text-muted-foreground">{problem}</p>}
          </div>
        )}

        {authorization && stage !== 'choose' && (
          <div className="space-y-2">
            {authorization.status === 'unknown' && <Alert variant="warning">{authorization.comments}</Alert>}
            {stage === 'awaiting' && authorization.status !== 'unknown' && <Alert>Wasfaty has the request. The payer usually answers within a minute — press Check answer.</Alert>}
            {stage === 'declined' && <Alert variant="destructive">The payer did not approve any line. Release the prescription so the patient can go to another pharmacy.</Alert>}
            {stage === 'done' && authorization.claim && (
              <Alert variant="success" title="Dispensed and claimed">
                Claim <span className="font-mono">{authorization.claim.claimId}</span> for {riyal(authorization.claim.netMinor)} was accepted.
              </Alert>
            )}
            <ul className="divide-y rounded-md border text-sm">
              {authorization.lines.map((line) => (
                <li key={line.lineNo} className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <span className="font-medium">{line.tradeName ?? line.tradeCode}</span>
                  <span className="text-muted-foreground">
                    {line.packs} pack{line.packs === 1 ? '' : 's'} · {riyal(line.netMinor)}
                  </span>
                  <Badge variant={statusTone(line.status)} className="ms-auto">
                    {STATUS_TEXT[line.status]}
                    {line.paymentMinor !== null && line.status !== 'requested' ? ` · ${riyal(line.paymentMinor)}` : ''}
                  </Badge>
                  {(line.comments || line.denialCode) && (
                    <p className="w-full text-xs text-muted-foreground">
                      {[line.denialCode, line.comments].filter(Boolean).join(' — ')}
                    </p>
                  )}
                </li>
              ))}
            </ul>
            {stage === 'claim' && (
              <div className="space-y-2 pt-1">
                <p className="text-xs text-muted-foreground">Optional: what the patient is told about each approved medicine. The claim is sent under your licence, so the same pharmacist who asked has to finish it.</p>
                {approvedLines(authorization).map((line) => (
                  <Field key={line.lineNo} label={`Instructions — ${line.tradeName ?? line.tradeCode}`}>
                    <Input
                      value={instructions[line.lineNo] ?? ''}
                      onChange={(e) => setInstructions((current) => ({ ...current, [line.lineNo]: e.target.value }))}
                      maxLength={500}
                      placeholder={prescription?.lines.find((entry) => entry.lineNo === line.lineNo)?.instructionsEn ?? 'As directed'}
                    />
                  </Field>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}

function LineCard({ line, choice, onChange }: { line: WasfatyLineDto; choice: LineChoice | undefined; onChange: (next: LineChoice) => void }) {
  if (!choice) return null;
  const trade = tradeOf(line, choice);
  const problem = choice.included ? choiceProblem(line, choice) : null;
  return (
    <div className="space-y-2 rounded-md border p-3">
      <Checkbox
        checked={choice.included}
        disabled={line.trades.length === 0}
        onCheckedChange={(checked) => onChange({ ...choice, included: checked })}
        label={<span className="font-medium">{line.genericName ?? `Line ${line.lineNo}`}</span>}
        description={
          <>
            Prescribed {line.orderedQuantity ?? '—'}
            {line.durationDays ? ` for ${line.durationDays} days` : ''}
            {line.prn ? ' · as needed' : ''}
            {line.refills > 0 ? ` · ${line.refills} refill${line.refills === 1 ? '' : 's'}` : ''}
            {line.instructionsEn ? ` · ${line.instructionsEn}` : ''}
          </>
        }
      />
      {line.trades.length === 0 && <p className="text-xs text-destructive">Wasfaty offered no medicine for this line.</p>}
      {choice.included && trade && (
        <div className="grid gap-3 ps-6 sm:grid-cols-[minmax(0,2fr)_6rem_minmax(0,2fr)]">
          <Field label="Medicine">
            <Select value={choice.tradeCode} onChange={(e) => onChange(withTrade(line, choice, e.target.value))}>
              {line.trades.map((option) => (
                <option key={option.tradeCode} value={option.tradeCode}>
                  {option.tradeName ?? option.tradeCode}
                  {option.isAlternative ? ' (alternative)' : ''}
                  {option.minimumQuantity ? ` · ${option.minimumQuantity} ${option.unitType ?? ''} per pack` : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Packs">
            <Input type="number" min={1} step={1} value={Number.isFinite(choice.packs) ? choice.packs : ''} onChange={(e) => onChange({ ...choice, packs: e.target.value === '' ? Number.NaN : Number(e.target.value) })} />
          </Field>
          {trade.isAlternative ? (
            <Field label="Why an alternative">
              <Select value={choice.alternativeReason} onChange={(e) => onChange({ ...choice, alternativeReason: e.target.value })}>
                <option value="">Choose a reason</option>
                {WASFATY_ALTERNATIVE_REASONS.map((reason) => (
                  <option key={reason.code} value={reason.code}>
                    {reason.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <div />
          )}
          <div className="flex flex-wrap items-center gap-2 text-xs sm:col-span-3">
            <span className="font-medium">{riyal(lineTotalMinor(trade, choice.packs))}</span>
            {trade.isHighAlert && <Badge variant="warning">High alert</Badge>}
            {trade.isHazardous && <Badge variant="warning">Hazardous</Badge>}
            {problem && <span className="text-destructive">{problem}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
